import { TDocumentDefinitions, Table } from 'pdfmake/interfaces'

import { colors } from '@astro-ui/fondations'
import { dateFormat } from 'utils/helpers/date'

import { TermsAndConditionsDataInterface } from 'features/Enterprise/SKP/redux/tnc'
import { SKPDetailsInterface } from 'features/Enterprise/SKP/utils/apiList'

import generatePromotionType from './generatePromotionType'

export const generatePdfTemplate = ({
  skpDetails,
  tnc,
  pdfTable,
}: {
  skpDetails: SKPDetailsInterface
  tnc: TermsAndConditionsDataInterface
  pdfTable: Table
}): TDocumentDefinitions => {
  const isInternalPromotionType = skpDetails.promotion_type?.toLowerCase()?.includes('internal')

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    content: [
      {
        text: 'SURAT KERJASAMA PROMOSI PT ASTRO TECHNOLOGIES INDONESIA',
        alignment: 'center',
        fontSize: 12,
        bold: true,
        style: ['Tet', 'Tot'],
      },
      {
        text: `DENGAN ${skpDetails?.vendor_name?.toUpperCase()}`,
        alignment: 'center',
        fontSize: 12,
        bold: true,
        marginBottom: 15,
      },
      {
        columns: [
          {
            stack: [
              {
                columns: [
                  {
                    width: 100,
                    text: 'Nomor SKP',
                    bold: true,
                    fontSize: 9,
                  },
                  {
                    width: '*',
                    text: `: ${skpDetails.skp_no}`,
                    fontSize: 9,
                  },
                ],
                marginBottom: 3,
              },
              {
                columns: [
                  {
                    width: 100,
                    text: 'Nama Kegiatan',
                    bold: true,
                    fontSize: 9,
                  },
                  {
                    width: '*',
                    text: `: ${skpDetails.campaign_name}`,
                    fontSize: 9,
                  },
                ],
                marginBottom: 3,
              },
              {
                columns: [
                  {
                    width: 100,
                    text: 'Tipe Promosi',
                    bold: true,
                    fontSize: 9,
                  },
                  {
                    text: `: ${generatePromotionType(skpDetails.promotion_type, true)}`,
                    fontSize: 9,
                  },
                ],
                marginBottom: 3,
              },
              {
                columns: [
                  {
                    width: 100,
                    text: 'Nama Vendor',
                    bold: true,
                    fontSize: 9,
                  },
                  {
                    text: `: ${skpDetails.vendor_name}`,
                    fontSize: 9,
                  },
                ],
                marginBottom: 3,
              },
              {
                columns: [
                  {
                    width: 100,
                    text: 'Tanggal Mulai',
                    bold: true,
                    fontSize: 9,
                  },
                  {
                    text: `: ${dateFormat({
                      date: new Date(skpDetails.start_date),
                      format: 'DD-MMMM-YYYY HH:mm',
                      utc: false,
                    })}`,
                    fontSize: 9,
                  },
                ],
                marginBottom: 3,
              },
              {
                columns: [
                  {
                    width: 100,
                    text: 'Tanggal Selesai',
                    bold: true,
                    fontSize: 9,
                  },
                  {
                    text: `: ${dateFormat({
                      date: new Date(skpDetails.end_date),
                      format: 'DD-MMMM-YYYY HH:mm',
                      utc: false,
                    })}`,
                    fontSize: 9,
                  },
                ],
                marginBottom: 3,
              },
              {
                columns: [
                  {
                    width: 100,
                    text: isInternalPromotionType ? 'Budget Type' : 'Metode Claim',
                    bold: true,
                    fontSize: 9,
                  },
                  {
                    text: `: ${
                      isInternalPromotionType
                        ? skpDetails.budget_type || '-'
                        : skpDetails.reimbursement_method
                    }`,
                    fontSize: 9,
                  },
                ],
                marginBottom: 3,
              },
            ],
          },
          {
            stack: isInternalPromotionType
              ? []
              : [
                  {
                    columns: [
                      {
                        width: 100,
                        text: 'Nama Billing',
                        bold: true,
                        fontSize: 9,
                      },
                      {
                        width: '*',
                        text: `: ${skpDetails.billing_name}`,
                        fontSize: 9,
                      },
                    ],
                    marginBottom: 3,
                  },
                  {
                    columns: [
                      {
                        width: 100,
                        text: 'Alamat Billing',
                        bold: true,
                        fontSize: 9,
                      },
                      {
                        width: '*',
                        text: `: ${skpDetails.billing_address}`,
                        fontSize: 9,
                      },
                    ],
                    marginBottom: 3,
                  },
                ],
          },
        ],
      },
      {
        table: pdfTable,
        marginTop: 10,
        marginBottom: 10,
      },
      {
        columns: [
          {
            width: 'auto',
            text: 'Ketentuan',
            fontSize: 9,
          },
          {
            width: 'auto',
            text: ':',
            fontSize: 9,
          },
          {
            width: '*',
            stack: [
              {
                text: tnc.tnc_description,
                alignment: 'justify',
                fontSize: 9,
              },
            ],
            fontSize: 15,
          },
        ],
        columnGap: 5,
      },
      {
        columns: [
          {
            stack: [
              {
                text: 'Disetujui oleh,',
                marginBottom: 70.5,
                fontSize: 9,
              },
              {
                text: `${skpDetails.astro_pic_signed}`,
                fontSize: 9,
                marginBottom: 3,
              },
              {
                text: skpDetails.astro_pic_role,
                italics: true,
                fontSize: 9,
                marginBottom: 3,
              },
              {
                text: 'PT. ASTRO TECHNOLOGIES INDONESIA',
                bold: true,
                fontSize: 11,
              },
            ],
          },
          {
            stack: [
              {
                text: skpDetails.vendor_pic_signed,
                fontSize: 9,
                marginBottom: 3,
              },
              {
                text: skpDetails.vendor_pic_role,
                italics: true,
                fontSize: 9,
                marginBottom: 3,
              },
              {
                text: skpDetails.vendor_name.toUpperCase(),
                bold: true,
                fontSize: 11,
              },
            ],
            marginTop: 81,
          },
        ],
        marginLeft: 100,
        marginTop: 20,
        unbreakable: true,
      },
    ],
    footer: (currentPage, pageCount) => {
      const pageTxt = `Halaman ${currentPage.toString()} dari ${pageCount}`
      return [
        {
          text: pageTxt,
          fontSize: 12,
          alignment: 'right',
          color: colors.textSoft,
          marginRight: 20,
          marginTop: 10,
        },
      ]
    },
    styles: {
      tableHead: {
        bold: true,
        alignment: 'center',
        fontSize: 9,
        fillColor: colors.skpTableHeadBg,
      },
      tableContent: {
        alignment: 'center',
        fontSize: 9,
      },
      mergeCellMargin: {
        marginLeft: 5,
        marginRight: 5,
        marginTop: 20,
      },
      mt10: {
        marginTop: 10,
      },
      m5: {
        margin: 5,
      },
    },
  }
}
